import { DragDropContext, Draggable, Droppable } from '@hello-pangea/dnd';
import { AlertModal, DestructiveModal, Text, useOpenModal } from '@modusign/design-system';
import { useFields } from '@modusign/react-signing';
import { useUIState } from '@modusign/react-signing/setup';
import { SignerToReference } from '@modusign/react-signing/types';
import { Dispatch, SetStateAction, useCallback, useMemo, useState } from 'react';
import { useFormContext } from 'react-hook-form';
import { v4 } from 'uuid';

import SignerCardsDeleteEmptySignersButton from '@app/components/SignerCardsDeleteEmptySignersButton';
import SignerCardsSignerAddButton from '@app/components/SignerCardsSignerAddButton';
import SignerCardsSignerCount from '@app/components/SignerCardsSignerCount';
import { MAX_SIGNER_COUNT } from '@app/constants/documentSetup';
import SigningProgressTypeSelectionCard from '@app/containers/SigningTypeSelectionCard';
import TemplateSignerCard from '@app/features/template/components/TemplateSignerCard';
import { reorder } from '@app/libs/dnd/reorder';

import './TemplateSignerCards.scss';

interface TemplateSignerCardsProps {
  signerIds: string[];
  onSignerIdsChange: Dispatch<SetStateAction<string[]>>;
}

function TemplateSignerCards({ signerIds, onSignerIdsChange }: TemplateSignerCardsProps) {
  const openModal = useOpenModal();
  const { setInOrder, ui } = useUIState();
  const [isSignerRemoveWarningModalOpen, setIsSignerRemoveWarningModalOpen] = useState(false);

  const { fields } = useFields();

  const { getValues, trigger } = useFormContext<Record<string, SignerToReference>>();

  const signerValues = getValues();

  const nameAndContactEmptySignerIds = useMemo(() => {
    return signerIds
      .map((id): string | null => {
        const signerValue = signerValues[id];
        if (signerValue && (signerValue.name || signerValue.contact || signerValue.role)) {
          return null;
        }
        return id;
      })
      .filter((id) => !!id);
  }, [signerIds, signerValues]);

  const onSignerRemove = useCallback(
    (signerId: string) => {
      if (signerIds.length > 1) {
        onSignerIdsChange((ids) => ids.filter((id) => id !== signerId));
      }
    },
    [onSignerIdsChange, signerIds.length]
  );

  const onSignerAdd = useCallback(() => {
    if (signerIds.length < MAX_SIGNER_COUNT) {
      onSignerIdsChange((signerIds) => [...signerIds, v4()]);
    } else {
      openModal((modalProps) => (
        <AlertModal title="서명자 추가 안내" variant="warning" {...modalProps} maxWidth="xs">
          서명자는 최대 {MAX_SIGNER_COUNT}명까지 추가할 수 있습니다.
        </AlertModal>
      ));
    }
  }, [onSignerIdsChange, openModal, signerIds.length]);

  const onSignerSequenceChange = useCallback(
    (oldIndex: number, bias: number) => {
      const newIndex = oldIndex + bias;
      if (newIndex >= 0 && newIndex < signerIds.length) {
        onSignerIdsChange(reorder(signerIds, oldIndex, newIndex));
      }
    },
    [onSignerIdsChange, signerIds]
  );

  const handleRemoveAllNameAndMeansEmptySigners = useCallback(() => {
    if (signerIds.length > 1) {
      onSignerIdsChange((ids) => {
        const shouldRemainFirstSigner = signerIds.length === nameAndContactEmptySignerIds.length;
        return ids.filter(
          (id, index) =>
            !nameAndContactEmptySignerIds.includes(id) || (shouldRemainFirstSigner && index === 0)
        );
      });
      setTimeout(() => trigger(), 100);
    }
  }, [nameAndContactEmptySignerIds, onSignerIdsChange, signerIds.length, trigger]);

  return (
    <div className="TemplateSignerCards Card Card--theme-editor Card--primary box-2x">
      <SigningProgressTypeSelectionCard isInOrder={ui.isInOrder} onChange={setInOrder} />
      <SignerCardsSignerCount count={signerIds.length} />
      <DragDropContext
        onDragEnd={(result) => {
          if (!result.destination) {
            return;
          }

          const ids = reorder(signerIds, result.source.index, result.destination.index);
          onSignerIdsChange(ids);
        }}
      >
        <Droppable droppableId="signers">
          {(provided) => (
            <div {...provided.droppableProps} ref={provided.innerRef}>
              {signerIds.map((id, index) => (
                <Draggable draggableId={id} index={index} key={id}>
                  {(provided) => (
                    <div
                      ref={provided.innerRef}
                      {...provided.draggableProps}
                      {...provided.dragHandleProps}
                      style={{
                        userSelect: 'none',
                        marginBottom: '12px',
                        ...provided.draggableProps.style,
                      }}
                    >
                      <TemplateSignerCard
                        id={id}
                        indexNumber={index}
                        isInOrder={ui.isInOrder}
                        key={id}
                        onSequenceChange={onSignerSequenceChange}
                        onSignerRemove={signerIds.length > 1 ? onSignerRemove : undefined}
                      />
                    </div>
                  )}
                </Draggable>
              ))}
              {provided.placeholder}
            </div>
          )}
        </Droppable>
      </DragDropContext>
      <SignerCardsSignerAddButton onClick={onSignerAdd} />
      {nameAndContactEmptySignerIds.length > 0 && signerIds.length > 1 && (
        <SignerCardsDeleteEmptySignersButton
          onClick={() => {
            const shouldWarnRemovingField = nameAndContactEmptySignerIds.some((signerId) =>
              fields.some((field) => field.participantReference.id === signerId)
            );

            if (shouldWarnRemovingField) {
              setIsSignerRemoveWarningModalOpen(true);
            } else {
              handleRemoveAllNameAndMeansEmptySigners();
            }
          }}
        />
      )}
      <DestructiveModal
        closeButtonLabel="취소"
        confirmButtonLabel="삭제"
        isOpen={isSignerRemoveWarningModalOpen}
        title="서명자 삭제 안내"
        width="xs"
        onClose={() => setIsSignerRemoveWarningModalOpen(false)}
        onConfirm={() => {
          setIsSignerRemoveWarningModalOpen(false);
          handleRemoveAllNameAndMeansEmptySigners();
        }}
      >
        <Text textAlign="center">서명자를 삭제하면 설정했던 입력란도 함께 삭제됩니다.</Text>
      </DestructiveModal>
    </div>
  );
}

export default TemplateSignerCards;
